import React, { useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../ui/sheet';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Camera, Microphone, X } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { GabyPhotoAnalyzer } from '../csps/GabyPhotoAnalyzer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function RéserveFormSheet({
  open, onOpenChange, chantiers,
  editingRéserve, formData, setFormData, submitting,
  selectedPhotos, setSelectedPhotos,
  isRecording, transcription,
  onSubmit, onStartRecording, onStopRecording,
}) {
  const fileInputRef = useRef(null);
  // Photos pre-uploadees (avec file_id) pour permettre l'analyse Gaby inline
  const [uploadedPhotos, setUploadedPhotos] = useState([]); // [{ file_id, filename, previewUrl }]
  const [uploadingPhotoIdx, setUploadingPhotoIdx] = useState(-1);

  // Reset lorsque le sheet se ferme ou change de reserve
  useEffect(() => {
    if (!open) {
      setUploadedPhotos([]);
      setUploadingPhotoIdx(-1);
    }
  }, [open, editingRéserve?.id]);

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedPhotos(prev => [...prev, ...files]);
    // Upload immediat pour permettre l'analyse Gaby avant soumission
    if (formData.chantier_id) {
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        setUploadingPhotoIdx(files.length - i - 1);
        try {
          const fd = new FormData();
          fd.append('file', f);
          const r = await axios.post(
            `${API}/upload?chantier_id=${formData.chantier_id}`,
            fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
          );
          setUploadedPhotos(prev => [...prev, {
            file_id: r.data.file_id || r.data.id,
            filename: f.name,
            previewUrl: URL.createObjectURL(f),
          }]);
        } catch (err) {
          toast.error(`Upload photo échoué: ${f.name}`);
        }
      }
      setUploadingPhotoIdx(-1);
    }
  };

  const removePhoto = (index) => {
    setSelectedPhotos(prev => prev.filter((_, i) => i !== index));
    setUploadedPhotos(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" aria-describedby="réserve-form-desc">
        <SheetHeader>
          <SheetTitle>{editingRéserve ? 'Modifiér la réserve' : 'Nouvelle réserve'}</SheetTitle>
          <p id="réserve-form-desc" className="text-sm text-[#4B5563]">
            {editingRéserve ? 'Modifiéz les informations de la réserve' : 'Creez une nouvelle réserve pour un chantier'}
          </p>
        </SheetHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-6">
          <div>
            <Label>Chantier *</Label>
            <Select value={formData.chantier_id}
              onValueChange={(value) => setFormData({...formData, chantier_id: value})}
              disabled={!!editingRéserve}>
              <SelectTrigger className="mt-1 h-14" data-testid="réserve-chantier-select">
                <SelectValue placeholder="Sélectionner un chantier" />
              </SelectTrigger>
              <SelectContent position="popper" className="z-[100]">
                {(chantiers || []).map(c => c && (
                  <SelectItem key={c.id} value={c.id} className="py-3">{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="title">Titre *</Label>
            <Input id="title" value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Ex: Fissure mur RDC" required
              data-testid="réserve-title-input" className="mt-1 h-14" />
          </div>

          <div>
            <Label htmlFor="location">Localisation *</Label>
            <Input id="location" value={formData.location}
              onChange={(e) => setFormData({...formData, location: e.target.value})}
              placeholder="Ex: Bâtiment A, Niveau 2, Local 201" required
              data-testid="réserve-location-input" className="mt-1 h-14" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Priorite</Label>
              <Select value={formData.priority}
                onValueChange={(value) => setFormData({...formData, priority: value})}>
                <SelectTrigger className="mt-1 h-14" data-testid="réserve-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]">
                  <SelectItem value="high" className="py-3">Haute</SelectItem>
                  <SelectItem value="medium" className="py-3">Moyenne</SelectItem>
                  <SelectItem value="low" className="py-3">Basse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="lot">Lot</Label>
              <Input id="lot" value={formData.lot}
                onChange={(e) => setFormData({...formData, lot: e.target.value})}
                placeholder="Ex: Gros oeuvre"
                data-testid="réserve-lot-input" className="mt-1 h-14" />
            </div>
          </div>

          <div>
            <Label htmlFor="responsible">Entreprise responsable</Label>
            <Input id="responsible" value={formData.responsible}
              onChange={(e) => setFormData({...formData, responsible: e.target.value})}
              placeholder="Ex: SARL Construction"
              data-testid="réserve-responsible-input" className="mt-1 h-14" />
          </div>

          <div>
            <Label htmlFor="description">Description *</Label>
            <Textarea id="description" value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Decrivez la réserve..." rows={3} required
              data-testid="réserve-description-input" className="mt-1" />
          </div>

          {/* Voice Note - only for new réserves */}
          {!editingRéserve && (
            <div>
              <Label>Note vocale</Label>
              <div className="mt-1 flex gap-2">
                <Button type="button" variant={isRecording ? "destructive" : "outline"}
                  onClick={isRecording ? onStopRecording : onStartRecording}
                  className="flex-1 h-14" data-testid="voice-record-btn">
                  <Microphone size={20} className="mr-2" />
                  {isRecording ? 'Arreter' : 'Enregistrér'}
                </Button>
              </div>
              {transcription && (
                <div className="mt-2 p-3 bg-gray-50 rounded-md text-sm">
                  <p className="font-medium text-[#111827] mb-1">Transcription:</p>
                  <p className="text-[#4B5563]">{transcription}</p>
                </div>
              )}
            </div>
          )}

          {/* Photos - only for new réserves */}
          {!editingRéserve && (
            <div>
              <Label>Photos</Label>
              <div className="mt-1 space-y-2">
                <input ref={fileInputRef} type="file" accept="image/*" multiple
                  onChange={handlePhotoSelect} className="hidden" />
                <Button type="button" variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full h-14" data-testid="photo-upload-btn">
                  <Camera size={20} className="mr-2" /> Ajouter des photos
                </Button>
                {selectedPhotos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {selectedPhotos.map((photo, index) => (
                      <div key={`${photo.name}-${index}`} className="relative aspect-square rounded-md overflow-hidden bg-gray-100">
                        <img src={URL.createObjectURL(photo)} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                        {uploadingPhotoIdx === index && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="h-4 w-4 animate-spin text-white" />
                          </div>
                        )}
                        <button type="button" onClick={() => removePhoto(index)}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full">
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Analyse Gaby inline - visible uniquement pour utilisateurs CSPS avec Gaby actif */}
                {uploadedPhotos.length > 0 && formData.chantier_id && (
                  <GabyPhotoAnalyzer
                    photos={uploadedPhotos.map(p => ({ id: p.file_id, filename: p.filename }))}
                    chantierId={formData.chantier_id}
                    notesContext={formData.title || ''}
                    variant="full"
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-14">Annuler</Button>
            <Button type="submit" disabled={submitting || !formData.chantier_id}
              className="flex-1 h-14 bg-[#0055FF] hover:bg-blue-700" data-testid="réserve-submit-btn">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (editingRéserve ? 'Enregistrér' : 'Créer')}
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  );
}
